import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { showError } from '../../0_presentation/core/components/feedback/snackbar';
import { Client, ClientSummary } from '../../2_domain/client/client';
import { Staff } from '../../2_domain/staff/staff';
import ITestRepository from '../../2_domain/test/iTestRepository';
import { TestCategory, TestItemSummary } from '../../2_domain/test/test';
import Get from '../../lib/di/get';
import promiseResolver from '../../lib/util/promiseResolver';

const useTestAddPageController = () => {
  const testRepo = Get.find<ITestRepository>('ITestRepository');
  const navigate = useNavigate();
  const theme = useTheme();

  const state: { client: Client } = useLocation().state;
  const initialClient = state?.client;
  const initialStep = state?.client ? 1 : 0;

  const maxStep = 3;

  const [client, setClient] = useState<ClientSummary>(initialClient);
  const [testItems, setTestItems] = useState<TestItemSummary[]>([]);
  const [receptionist, setReceptionist] = useState<Staff>();
  const [attendingPhysician, setAttendingPhysician] = useState<Staff>();
  const [memo, setMemo] = useState('');
  const [step, setStep] = useState(initialStep);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [testCategories, setTestCategories] = useState<TestCategory[]>([]);

  let buildSider = maxStep !== step;

  const onPrev = () => {
    setStep((prev) => {
      if (prev === 0) {
        return prev;
      } else {
        return prev - 1;
      }
    });
  };

  const onNext = () => {
    setStep((prev) => {
      if (prev === maxStep) {
        return prev;
      } else {
        return prev + 1;
      }
    });
  };

  const addTestItem = (testItem: TestItemSummary) => {
    setTestItems((prev) => [testItem, ...prev]);
  };

  const removeTestItem = (testItem: TestItemSummary) => {
    setTestItems((prev) => {
      const newItems = [...prev];
      return newItems.filter((item) => item.id !== testItem.id);
    });
  };

  const saveTest = async () => {
    if (
      client &&
      testItems.length !== 0 &&
      receptionist &&
      attendingPhysician
    ) {
      await promiseResolver({
        promise: testRepo.createTest(
          client.id,
          testItems.map((item: any) => item),
          receptionist.id,
          attendingPhysician.id,
          memo
        ),
        successCallback: () => navigate(0),
      });
    } else {
      showError(theme.texts.pleaseCheckTheInformationYouEntered);
      return;
    }
  };

  useEffect(() => {
    promiseResolver({
      promise: testRepo.getTestCategories(),
      successCallback: (data) => {
        setTestCategories(data);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 0) {
      setNextDisabled(client === undefined);
    } else if (step === 1) {
      setNextDisabled(testItems.length === 0);
    } else if (step === 2) {
      setNextDisabled(
        receptionist === undefined || attendingPhysician === undefined
      );
    } else {
      setNextDisabled(false);
    }
  }, [step, client, receptionist, testItems, attendingPhysician]);

  return {
    testCategories,
    client,
    setClient,
    receptionist,
    testItems,
    addTestItem,
    removeTestItem,
    memo,
    setMemo,
    onPrev,
    onNext,
    step,
    buildSider,
    nextDisabled,
    saveTest,
    attendingPhysician,
    setReceptionist,
    setAttendingPhysician,
  };
};

export default useTestAddPageController;
